import React, { useMemo } from "react";

import AppText from "../../design-system/components/AppText";
import AppTooltip from "../../design-system/components/AppTooltip";
import {
  asNumber,
  buildDonutSegments,
  calendarDate,
  validDate,
} from "./dashboardModel";

// Ferien und Absenzen sind keine Abteilung: Sie tragen dieselbe Farbe wie im
// Tagesdiagramm daneben. Die Variablen stehen im Wurzelelement des Dashboards
// (client/src/scenes/admin_dashboard/dashboard.css) — so bleiben beide
// Diagramme automatisch gleich.
const SERIES_COLORS = {
  holiday: "var(--admin-dashboard-hours-holiday)",
  absence: "var(--admin-dashboard-hours-absence)",
};

export function DepartmentDonut({
  items,
  total,
  getDepartmentColor,
  formatHours,
  emptyLabel,
  totalLabel,
  centerValue = null,
  centerUnit = "",
  layout = "split",
  renderValue = null,
  showPercent = true,
}) {
  // Alle Bereiche einzeln — nichts wird zu "Weitere" zusammengefasst, die
  // Legende scrollt stattdessen.
  // Ferien und Absenzen zuerst: ihre Farbe kommt aus dem Dashboard-Token, nicht
  // aus der Abteilungspalette. Danach eine vom Backend gesetzte Farbe.
  const legendColor = (item) => (
    SERIES_COLORS[item?.kind] || item?.color || getDepartmentColor(item.department)
  );
  const segments = buildDonutSegments(items, legendColor);
  const hasCenterValue = centerValue !== null && centerValue !== undefined && centerValue !== "";
  // Ab vierstelligen Stunden wird aus "384.8" ein "3'214.8" — die Zahl muss
  // dafuer schrumpfen, sonst schneidet der Donut sie ab.
  const centerLength = hasCenterValue ? String(centerValue).length : 0;
  const centerSizeClass = centerLength > 6 ? " is-xlong" : centerLength > 5 ? " is-long" : "";
  const valueFor = renderValue || ((item) => formatHours(item.hours));
  // Der Titel steht schon in der Donut-Mitte, deshalb nie doppelt vorlesen lassen.
  const accessibleLabel = (hasCenterValue
    ? [centerValue, centerUnit, centerUnit === totalLabel ? "" : totalLabel]
    : [formatHours ? formatHours(total) : total, totalLabel]
  ).filter((part) => part !== "" && part !== null && part !== undefined).join(" ");

  return (
    <div className={`admin-dashboard-department-breakdown admin-dashboard-department-breakdown--${layout}`}>
      <div
        className="admin-dashboard-department-donut"
        role="img"
        aria-label={accessibleLabel}
      >
        <svg viewBox="0 0 120 120" aria-hidden="true">
          {segments.map((segment) => (
            <circle
              key={segment.key}
              cx="60"
              cy="60"
              r="49"
              pathLength="100"
              stroke={segment.color}
              strokeDasharray={`${Math.min(100, segment.length + 0.12)} ${Math.max(0, 100 - segment.length)}`}
              strokeDashoffset={-segment.start}
            />
          ))}
        </svg>
        {hasCenterValue ? (
          <span className="admin-dashboard-department-donut__center" aria-hidden="true">
            <strong className={`admin-dashboard-department-donut__center-value${centerSizeClass}`}>
              {centerValue}
            </strong>
            {centerUnit ? (
              <span className="admin-dashboard-department-donut__center-unit">{centerUnit}</span>
            ) : null}
          </span>
        ) : null}
      </div>
      <div className="admin-dashboard-department-list">
        {items.length > 0 ? items.map((item, index) => (
          <div className="admin-dashboard-department-list__row" key={`${item.department}-${index}`}>
            <i style={{ background: legendColor(item) }} aria-hidden="true" />
            <AppText variant="body-2" truncate>{item.department || "–"}</AppText>
            <AppText variant="label-3" className="admin-dashboard-department-list__hours">
              {valueFor(item)}
            </AppText>
            {showPercent ? (
              <AppText variant="body-3" tone="secondary">
                {Math.round(asNumber(item.percent))}%
              </AppText>
            ) : null}
          </div>
        )) : (
          <AppText variant="body-2" tone="secondary">{emptyLabel}</AppText>
        )}
      </div>
    </div>
  );
}

function planningCellShare(day) {
  if (!day.hasDemand) {
    return { covered: day.planned > 0 ? 100 : 0, open: 0 };
  }
  const required = Math.max(day.required, 1);
  const covered = Math.min(day.covered, day.required);
  return {
    covered: (covered / required) * 100,
    open: (Math.min(day.missing, day.required) / required) * 100,
  };
}

export function PlanningStatusGrid({
  planningStatus,
  getDepartmentColor,
  locale,
  labels,
}) {
  const departments = planningStatus?.departments || [];
  const dates = planningStatus?.dates;

  const dayHeaders = useMemo(() => (dates || []).map((date, index) => {
    const parsed = calendarDate(date);
    return {
      date,
      isToday: index === 0,
      weekday: new Intl.DateTimeFormat(locale, { weekday: "short" }).format(parsed),
      day: parsed.getDate(),
      full: new Intl.DateTimeFormat(locale, {
        weekday: "long",
        day: "numeric",
        month: "long",
      }).format(parsed),
    };
  }), [dates, locale]);

  if (departments.length === 0 || dayHeaders.length === 0) {
    return (
      <div className="admin-dashboard-empty-state admin-dashboard-empty-state--chart">
        <AppText variant="body-2" tone="secondary">{labels.empty}</AppText>
      </div>
    );
  }

  return (
    <div
      className="admin-dashboard-planning"
      style={{ "--admin-dashboard-planning-days": dayHeaders.length }}
    >
      <div className="admin-dashboard-planning__table">
        <div className="admin-dashboard-planning__head" aria-hidden="true">
          <span className="admin-dashboard-planning__corner" />
          {dayHeaders.map((header) => (
            <span
              key={header.date}
              className={`admin-dashboard-planning__day${header.isToday ? " is-today" : ""}`}
            >
              {header.weekday} {header.day}
            </span>
          ))}
        </div>

        <div
          className="admin-dashboard-planning__rows"
          role="table"
          aria-label={labels.gridLabel}
        >
          {departments.map((department) => {
            const color = department.color || getDepartmentColor(department.department);
            return (
              <div
                className="admin-dashboard-planning__row"
                key={department.departmentId ?? department.department}
                role="row"
                style={{ "--admin-dashboard-department": color }}
              >
                <span className="admin-dashboard-planning__rowhead" role="rowheader">
                  <span className="admin-dashboard-planning__badge" aria-hidden="true">
                    {department.shorthand || department.department.slice(0, 3).toUpperCase()}
                  </span>
                  <span className="admin-dashboard-planning__department">
                    {department.department}
                  </span>
                </span>
                {department.days.map((day, index) => {
                  const header = dayHeaders[index];
                  const share = planningCellShare(day);
                  const statusLine = day.hasDemand
                    ? labels.coveredOf({ covered: day.covered, required: day.required })
                    : labels.noDemand({ planned: day.planned });
                  // Bewusst ohne Stundenangabe: relevant ist, welche Schicht offen ist.
                  const openLines = day.openShifts.map((shift) => [
                    shift.shorthand || shift.shiftName,
                    labels.openShifts({ count: shift.missing }),
                  ].filter(Boolean).join(" · "));
                  const ariaLabel = [
                    `${department.department} · ${header?.full || day.date}`,
                    statusLine,
                    ...openLines,
                  ].join(". ");

                  return (
                    <span
                      key={day.date}
                      className={`admin-dashboard-planning__cell${header?.isToday ? " is-today" : ""}`}
                      role="cell"
                      aria-label={ariaLabel}
                    >
                      <AppTooltip
                        placement="top"
                        content={(
                          <span className="admin-dashboard-planning__tip">
                            <strong>{department.department} · {header?.full || day.date}</strong>
                            <span>{statusLine}</span>
                            {openLines.map((line) => (
                              <span key={line} className="admin-dashboard-planning__tip-open">{line}</span>
                            ))}
                          </span>
                        )}
                      >
                        <span className="admin-dashboard-planning__gauge" tabIndex={0}>
                          <span className="admin-dashboard-planning__bar" aria-hidden="true">
                            <i
                              className="admin-dashboard-planning__bar-covered"
                              style={{ width: `${share.covered}%` }}
                            />
                            <i
                              className="admin-dashboard-planning__bar-open"
                              style={{ width: `${share.open}%` }}
                            />
                          </span>
                          <span className="admin-dashboard-planning__value" aria-hidden="true">
                            {day.hasDemand ? (
                              <>
                                <b>{day.covered}</b>
                                <span className="admin-dashboard-planning__value-sep">/</span>
                                <b className={day.missing > 0 ? "is-open" : ""}>{day.required}</b>
                              </>
                            ) : (
                              <b className={day.planned > 0 ? "" : "is-empty"}>{day.planned}</b>
                            )}
                          </span>
                        </span>
                      </AppTooltip>
                    </span>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// Steht in der Kartenfusszeile neben dem Link, nicht im Rumpf — deshalb eigene
// Komponente statt Teil des Rasters.
export function PlanningStatusLegend({ coveredLabel, openLabel }) {
  return (
    <ul className="admin-dashboard-planning__legend">
      <li className="is-covered"><i aria-hidden="true" /><span>{coveredLabel}</span></li>
      <li className="is-open"><i aria-hidden="true" /><span>{openLabel}</span></li>
    </ul>
  );
}

/*
  Drei Stundenarten pro Tag, uebereinander gestapelt: Schicht, Ferien, Absenz.
  Die Skala richtet sich nach der hoechsten Tagessumme, damit gestapelte Balken
  nie ueber den Rahmen laufen.
*/
export function MonthlyHoursChart({ data, formatHours, locale, emptyLabel, ariaLabel, seriesLabels = {} }) {
  const sorted = useMemo(
    () => [...data].sort((left, right) => String(left?.date || "").localeCompare(String(right?.date || ""))),
    [data]
  );
  const series = [
    { key: "shift", label: seriesLabels.shift || "Schichtstunden" },
    { key: "holiday", label: seriesLabels.holiday || "Ferienstunden" },
    { key: "absence", label: seriesLabels.absence || "Absenzstunden" },
  ];
  const dayTotal = (item) => series.reduce((sum, entry) => sum + Math.max(0, asNumber(item?.[entry.key])), 0);
  const maxHours = Math.max(0, ...sorted.map(dayTotal));
  const axisStep = maxHours >= 15
    ? Math.max(5, Math.ceil(maxHours / 15) * 5)
    : Math.max(1, Math.ceil(maxHours / 3));
  const axisMax = axisStep * 3;
  const formatAxisValue = (value) => new Intl.NumberFormat(locale, {
    maximumFractionDigits: 0,
  }).format(value);

  if (sorted.length === 0 || maxHours === 0) {
    return (
      <div className="admin-dashboard-empty-state admin-dashboard-empty-state--chart">
        <AppText variant="body-2" tone="secondary">{emptyLabel}</AppText>
      </div>
    );
  }

  return (
    <div className="admin-dashboard-month-chart-wrap">
    <div className="admin-dashboard-month-chart">
      <div className="admin-dashboard-month-chart__grid" aria-hidden="true"><i /><i /><i /><i /></div>
      <div
        className="admin-dashboard-month-chart__bars"
        style={{ "--admin-dashboard-bar-count": sorted.length }}
        role="list"
        aria-label={ariaLabel}
      >
        {sorted.map((item, index) => {
          const date = validDate(item.date ? item.date + "T12:00:00" : null);
          const day = date ? date.getDate() : index + 1;
          const parts = series
            .map((entry) => ({ ...entry, hours: Math.max(0, asNumber(item?.[entry.key])) }))
            .filter((entry) => entry.hours > 0);
          const hours = dayTotal(item);
          const height = Math.max(hours > 0 ? 4 : 1, (hours / axisMax) * 100);
          const showLabel = day === 1 || day % 5 === 0 || index === sorted.length - 1;
          const dateLabel = date
            ? new Intl.DateTimeFormat(locale, { weekday: "long", day: "numeric", month: "long" }).format(date)
            : String(day);
          const tooltipLabel = [
            `${dateLabel}: ${formatHours(hours)}`,
            ...parts.map((entry) => `${entry.label}: ${formatHours(entry.hours)}`),
          ].join(" · ");
          return (
            <div
              key={item.date || index}
              className="admin-dashboard-month-chart__column"
              role="listitem"
            >
              <div className="admin-dashboard-month-chart__anchor" style={{ height: `${height}%` }}>
                <AppTooltip content={tooltipLabel} placement="top">
                  <span
                    className="admin-dashboard-month-chart__bar"
                    role="img"
                    aria-label={tooltipLabel}
                    tabIndex={0}
                  >
                    {parts.map((entry) => (
                      <i
                        key={entry.key}
                        className={`admin-dashboard-month-chart__segment is-${entry.key}`}
                        style={{ height: `${(entry.hours / hours) * 100}%` }}
                      />
                    ))}
                  </span>
                </AppTooltip>
              </div>
              <span className="admin-dashboard-month-chart__label" aria-hidden="true">
                {showLabel ? day : ""}
              </span>
            </div>
          );
        })}
      </div>
      <div className="admin-dashboard-month-chart__scale" aria-hidden="true">
        <span>{formatAxisValue(axisMax)}</span>
        <span>{formatAxisValue(axisStep * 2)}</span>
        <span>{formatAxisValue(axisStep)}</span>
        <span>0</span>
      </div>
    </div>
      <ul className="admin-dashboard-month-chart__legend">
        {series.map((entry) => (
          <li key={entry.key} className={`is-${entry.key}`}>
            <i aria-hidden="true" />
            <span>{entry.label}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
